import {FlowStateController} from "@nolimit/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimit/slot-game/bin/flowcontrol/FlowStatePresentation";
import {NineToFive} from "../NineToFive";
import {NineToFiveSound} from "../sound/NineToFiveSound";
import {gsap} from "gsap";
import {NineToFiveSymbol} from "../reels/symbols/NineToFiveSymbol";
import {GSAPUtils} from "../utils/GSAPUtils";
import {NineToFiveScatterSymbol} from "../reels/symbols/ScatterSymbol";
import {NineToFiveXNudgeSymbol} from "../reels/symbols/XNudgeSymbol";
import {NineToFiveStatics} from "../config/NineToFiveStatics";
import {APISetting} from "@nolimit/slot-launcher/bin/interfaces/APISettingsSystem";

export class NineToFiveNormalResultPresentation implements FlowStatePresentation {

    constructor() {    }

    public start(flowStateController:FlowStateController) {
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                flowStateController.setSkipCallback();
            }
        });

        tl.add(() => flowStateController.setSkipCallback(() => tl.seek("End", false)));
        const shouldSync = NineToFive.model.gameData.reels
            .reduce((a,b) => a.concat(b), [])
            .some((sym) => sym == "C");

        const isFastSpins = NineToFive.apiPlugin.settings.get(APISetting.FAST_SPIN);
        const isLuckyDraw = NineToFive.model.buyFeatureName == "LUCKY_DRAW";
        const stopAnimation = shouldSync
                              ? this.godModeStop()
                              : isLuckyDraw
                              ? this.luckyDrawStop()
                              : isFastSpins
                              ? this.fastStop()
                              : this.normalStop();
        const jurPadding = NineToFive.gameFlowController.calculatePaddingDuration(stopAnimation.duration());

        tl.add(NineToFive.baseGameReelView.reelModels
                                          .map((reel) => reel.disableXNudge()),
               `>${Math.max(jurPadding - 0.45 - 0.16, 0)}`);
        tl.add(stopAnimation, '>0.16');
        tl.addLabel("End");

        //tl.add(shouldSync ? this.godModeStop() : this.normalStop());
        tl.add(() => {
            flowStateController.finish()
        }, '>0.5');
    }

    private godModeStop():gsap.core.Timeline {
        const tl = gsap.timeline();


        NineToFive.baseGameReelView.reelModels
            .forEach((reel, index) => {
                tl.add(reel.stop(NineToFive.model.data.reels[index], false, true), '<');
                if (index == 4) {
                    tl.add(() => {NineToFive.soundPlayer.stopSound(NineToFiveSound.REEL_SPINNING)});
                }
            });
        tl.add(() => NineToFive.soundPlayer.playSound("winCapSymbolLanding"));
        tl.add(() => {}, '>0.5');
        return tl;
    }

    private playSymbolLandingSounds(symbols:NineToFiveSymbol[]):gsap.Callback[] {
        return (symbols.map((sym) => {
            let landingSound = "";
            switch(sym.symbolName) {
                case "MP":
                    landingSound = "multiplierPositiveLand";
                    break;
                case "MN":
                    landingSound = "multiplierNegativeLand";
                    break;
                case "OT":
                    landingSound = "overTimeLand";
                    break;
                case "W":
                    landingSound = "wildLand";
                    break;
                case "XW":
                    landingSound = "xWaysLand";
                    break;
                case "XN":
                    if ((<NineToFiveXNudgeSymbol>sym).isActualSymbol) {
                        landingSound = "xNudgeLand";
                    }
                    break;
                case "S":
                    landingSound = `scatter${NineToFiveScatterSymbol.scatterCount}`;
                    break;
            }
            return landingSound == "" ? () => {} : () => {NineToFive.soundPlayer.playSound(landingSound)};
        }))
    }

    private normalStop():gsap.core.Timeline {
        const landedScatters:NineToFiveScatterSymbol[] = [];
        const shouldPlayAnticipation = !NineToFive.model.gameData.wasFeatureBuy ||
                                       NineToFive.model.buyFeatureName == "LUCKY_DRAW";

        const tl = gsap.timeline();
        NineToFive.baseGameReelView.reelModels
            .forEach((reel, index) => {
                const reelStopTl = gsap.timeline();

                // timings
                const scatterCount = landedScatters.length;
                const stopDelay = shouldPlayAnticipation
                                  ? scatterCount == 3
                                    ? '<2.24'
                                    : scatterCount == 2
                                    ? '<1.12'
                                    : '<0.28'
                                  : '<0.28';

                // stop reels
                reelStopTl.add(reel.stop(NineToFive.model.data.reels[index]));
                if (index == 0) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING1)})
                } else {
                    if (scatterCount > 1) {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipationReelStop");});
                    } else {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING2)})
                    }
                }
                if (index == 4) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.stopSound(NineToFiveSound.REEL_SPINNING)});
                    reelStopTl.add(() => {NineToFive.soundPlayer.stopSound("anticipation");});
                    if (scatterCount > 1) {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipationFail");});
                    }
                }
                reelStopTl.add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()));
                reelStopTl.add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')

                const newScatter = <NineToFiveScatterSymbol>(reel.getAllVisibleSymbols().find((sym) => sym.symbolName == "S"));
                if (newScatter != undefined) {
                    landedScatters.push(newScatter);
                }
                // scatter anticipation
                if (index != 4 && landedScatters.length > 1 && shouldPlayAnticipation) {
                    if (landedScatters.length == 2 && newScatter != undefined) {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipation");}, '<');
                        reelStopTl.add(landedScatters.map((sym) => sym.startAnticipation()))
                    } else if (landedScatters.length == 3) {
                        reelStopTl.add(newScatter.startAnticipation())
                    }
                } else if (index == 4) {
                    reelStopTl.add(landedScatters.map((sym) => (<NineToFiveScatterSymbol>sym).stopAnticipation()));
                }

                tl.add(reelStopTl, stopDelay);
            });
        return tl;
    }

    // pure copy paste out of laziness, could probably refactor to reuse some code but its the final days of
    // the project so just copy pasting will due :melting_face:
    private fastStop():gsap.core.Timeline {
        const tl = gsap.timeline();
        NineToFive.baseGameReelView.reelModels
            .forEach((reel, index) => {
                const reelStopTl = gsap.timeline();
                // stop reels
                reelStopTl.add(reel.stop(NineToFive.model.data.reels[index]));
                if (index == 0) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING1)})
                } else {
                    reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING2)})
                }
                if (index == 4) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.stopSound(NineToFiveSound.REEL_SPINNING)});
                }
                reelStopTl.add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()));
                reelStopTl.add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')
                tl.add(reelStopTl, 0);
            });
        return tl;
    }

    private luckyDrawStop():gsap.core.Timeline {
        const landedScatters:NineToFiveScatterSymbol[] = [];

        const tl = gsap.timeline();
        NineToFive.baseGameReelView.reelModels
            .forEach((reel, index) => {
                const reelStopTl = gsap.timeline();

                // timings
                const scatterCount = landedScatters.length;
                const stopDelay = scatterCount == 3 ? '<2.24' : '<0.28';

                // stop reels
                reelStopTl.add(reel.stop(NineToFive.model.data.reels[index]));
                if (index == 0) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING1)})
                } else {
                    if (scatterCount == 3) {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipationReelStop");});
                    } else {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound(NineToFiveSound.REEL_LANDING2)})
                    }
                }
                if (index == 4) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.stopSound(NineToFiveSound.REEL_SPINNING)});
                    reelStopTl.add(() => {NineToFive.soundPlayer.stopSound("anticipation");});
                    if (scatterCount == 3) {
                        reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipationFail");});
                    }
                }
                reelStopTl.add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()));
                reelStopTl.add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')

                const newScatter = <NineToFiveScatterSymbol>(reel.getAllVisibleSymbols()
                                                                 .find((sym) => sym.symbolName == "S"));
                if (newScatter != undefined) {
                    landedScatters.push(newScatter);
                }

                if (index == 3 && landedScatters.length == 3) {
                    reelStopTl.add(() => {NineToFive.soundPlayer.playSound("anticipation");}, '<');
                    reelStopTl.add(landedScatters.map((sym) => sym.startAnticipation()))
                }
                if (index == 4) {
                    reelStopTl.add(landedScatters.map((sym) => (<NineToFiveScatterSymbol>sym).stopAnticipation()));
                }

                tl.add(reelStopTl, stopDelay);
            });
        return tl;
    }
}